import { lazy } from "react"
import { createHashRouter } from "react-router-dom"

import { ErrorBoundary } from "@/components/ErrorBoundary"
import { MobileRedirect } from "@/components/MobileRedirect/MobileRedirect"
import {
    DisclaimerInner,
    DisclaimerPage,
    EULAInner,
    EULAPage,
    PrivacyPolicyInner,
    PrivacyPolicyPage,
    TermsOfUseInner,
    TermsOfUsePage,
} from "@/pages/terms-and-policy/Terms"

const isProduction = import.meta.env.VITE_ENVIRONMENT_NAME === "production"

const Layout = lazy(() => import("@/template/layout"))
const DashboardPage = lazy(() => import("@/pages/dashboard"))
const ChatPage = lazy(() => import("@/pages/chat"))
const WorkflowsPage = lazy(() => import("@/pages/workflows"))
const WorkflowDetailsPage = lazy(() => import("@/pages/workflowDetails"))
const DocumentsPage = lazy(() => import("@/pages/documents"))
const ConversationsPage = lazy(() => import("@/pages/conversations"))
const SettingsPage = lazy(() => import("@/pages/settings"))
const NoPage = lazy(() => import("@/pages/noPage"))
const MobileOopsPage = lazy(() => import("@/pages/mobileOops"))
const ConnectorsSetupPage = lazy(() => import("@/pages/connectors"))
const HelpAndResources = lazy(() => import("@/pages/helpAndResources"))
const ChatProvider = lazy(() => import("@/components/NotificationCenter/ChatWebPubSubProvider"))
const NewsPage = lazy(() => import("@/pages/news"))
const ProjectsPage = lazy(() => import("@/pages/projects"))
const ProjectCreatePage = lazy(() => import("@/pages/projects/ProjectCreate"))
const ProjectDetailsPage = lazy(() => import("@/pages/projectDetails"))
const AdminPage = lazy(() => import("@/pages/admin"))
const DebugWelcomeModal = isProduction
    ? lazy(() => import("@/pages/noPage"))
    : lazy(() => import("@/test/DebugWelcomeModal"))

const DebugXeokit = isProduction ? lazy(() => import("@/pages/noPage")) : lazy(() => import("@/test/DebugXeokit"))

export enum RoutesPath {
    Root = "/",
    Dashboard = "/dashboard",
    Chat = "/chat",
    Workflows = "/workflows",
    WorkflowsDetails = "/workflow-details",
    WorkflowsDetailsWithId = "/workflow-details/:id",
    ChatId = "/chat/:id",
    Upload = "/upload",
    Documents = "/documents",
    DocumentDetails = "/documents/:id?/:name?",
    Conversations = "/conversations",
    Settings = "/settings",
    User = "/user",
    Connectors = "/connectors",
    TermsOfUse = "/terms-of-service",
    TermsOfService = "/terms-of-use",
    PrivacyPolicy = "/privacy-policy",
    Disclaimer = "/disclaimer",
    EULA = "/eula",
    PrivacyPolicyInner = "/privacy-policy-inner",
    EULAInner = "/eula-inner",
    DisclaimerInner = "/disclaimer-inner",
    TermsOfUseInner = "/terms-of-use-inner",
    HelpAndResources = "/help-and-resources",
    News = "/announcements",
    Projects = "/projects",
    ProjectCreate = "/projects/new",
    ProjectDetails = "/projects/:id",
    Admin = "/admin",
    DebugModal = "/debug-modal",
    DebugXeokit = "/debug-xeokit",
    NoPage = "*",
}

const router = createHashRouter([
    {
        element: (
            <ErrorBoundary>
                <Layout />
                <MobileRedirect />
            </ErrorBoundary>
        ),
        path: RoutesPath.Root,
        children: [
            {
                index: true,
                element: (
                    <ChatProvider>
                        <ChatPage />
                    </ChatProvider>
                ),
            },
            {
                path: RoutesPath.Dashboard,
                element: <DashboardPage />,
            },
            {
                path: RoutesPath.Chat,
                element: (
                    <ChatProvider>
                        <ChatPage />
                    </ChatProvider>
                ),
                children: [
                    {
                        path: RoutesPath.ChatId,
                        element: (
                            <ChatProvider>
                                <ChatPage />
                            </ChatProvider>
                        ),
                    },
                ],
            },
            {
                path: RoutesPath.Workflows,
                element: <WorkflowsPage />,
            },
            {
                path: RoutesPath.WorkflowsDetails,
                element: <WorkflowDetailsPage />,
            },
            {
                path: RoutesPath.WorkflowsDetailsWithId,
                element: <WorkflowDetailsPage />,
            },
            {
                path: RoutesPath.Documents,
                element: <DocumentsPage />,
            },
            {
                path: RoutesPath.DocumentDetails,
                element: <DocumentsPage />,
            },
            {
                path: RoutesPath.Conversations,
                element: <ConversationsPage />,
            },
            {
                path: RoutesPath.Settings,
                element: <SettingsPage />,
            },
            {
                path: RoutesPath.NoPage,
                element: <NoPage />,
            },
            {
                path: RoutesPath.Connectors,
                element: <ConnectorsSetupPage />,
            },
            {
                path: RoutesPath.PrivacyPolicyInner,
                element: <PrivacyPolicyInner />,
            },
            {
                path: RoutesPath.EULAInner,
                element: <EULAInner />,
            },
            {
                path: RoutesPath.DisclaimerInner,
                element: <DisclaimerInner />,
            },
            {
                path: RoutesPath.TermsOfUseInner,
                element: <TermsOfUseInner />,
            },
            {
                path: RoutesPath.HelpAndResources,
                element: <HelpAndResources />,
            },
            {
                path: RoutesPath.News,
                element: <NewsPage />,
            },
            {
                path: RoutesPath.Projects,
                element: <ProjectsPage />,
            },
            {
                path: RoutesPath.ProjectCreate,
                element: <ProjectCreatePage />,
            },
            {
                path: RoutesPath.ProjectDetails,
                element: <ProjectDetailsPage />,
            },
            {
                path: RoutesPath.Admin,
                element: <AdminPage />,
            },
            {
                path: RoutesPath.DebugModal,
                element: <DebugWelcomeModal />,
            },
            {
                path: RoutesPath.DebugXeokit,
                element: <DebugXeokit />,
            },
        ],
    },
    {
        path: "/mobile-oops",
        element: <MobileOopsPage />,
    },
    {
        path: RoutesPath.TermsOfUse,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.TermsOfService,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.PrivacyPolicy,
        element: <PrivacyPolicyPage />,
    },
    {
        path: RoutesPath.Disclaimer,
        element: <DisclaimerPage />,
    },
    {
        path: RoutesPath.EULA,
        element: <EULAPage />,
    },
])

export default router
